import React, { useState } from "react";
import PropTypes from "prop-types";
import NavItem from "../NavItem";
import ArticleIcon from "@mui/icons-material/Article";
import GroupsIcon from "@mui/icons-material/Groups";
import ComputerIcon from "@mui/icons-material/Computer";
import ReportIcon from "@mui/icons-material/Report";
import {
  LogoutOutlined,
  ChevronLeft,
  ChevronRight,
  ExpandMore,
  ChevronRight as ExpandRight,
} from "@mui/icons-material";
import { logout } from "store/slice/authentication";
import { useDispatch, useSelector } from "react-redux";
import { getMenuItems } from "../../config/routeConfig";

const SIDEBAR_COLLAPSED_KEY = "sidebarCollapsed";

// Icon mapping for dynamic loading
const iconMap = {
  ArticleIcon: ArticleIcon,
  GroupsIcon: GroupsIcon,
  ComputerIcon: ComputerIcon,
  ReportIcon: ReportIcon,
};

const Sidebar = ({ isCollapsed = false, onToggleCollapse }) => {
  const { role } = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const [openMenus, setOpenMenus] = useState({});

  const menuItems = getMenuItems(role);

  const toggleSubmenu = path => {
    setOpenMenus(prev => ({ ...prev, [path]: !prev[path] }));
  };

  const handleToggle = () => {
    const next = !isCollapsed;
    onToggleCollapse?.(next);
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, JSON.stringify(next));
    } catch {
      // ignore localStorage errors (e.g. private mode)
    }
  };

  return (
    <div
      className={`fixed py-4 px-0 bg-clip-border bg-black text-gray-700 h-screen shadow-xl shadow-blue-gray-900/5 flex flex-col transition-[width] duration-300 ease-in-out ${
        isCollapsed ? "w-16" : "w-48"
      }`}
    >
      <div
        className={`flex items-center gap-1 px-2 min-h-[40px] ${
          isCollapsed ? "mb-4 justify-center" : "mb-2 justify-between"
        }`}
      >
        {!isCollapsed && (
          <h5 className="py-[8px] px-[14px] block antialiased tracking-normal text-3xl font-semibold leading-snug text-white truncate">
            ATS
          </h5>
        )}
        <button
          type="button"
          onClick={handleToggle}
          className={`absolute right-0 p-1.5 rounded-md text-white hover:bg-white/10 transition-colors shrink-0 ${!isCollapsed ? "ml-auto" : ""}`}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <ChevronRight className="size-5" />
          ) : (
            <ChevronLeft className="size-5" />
          )}
        </button>
      </div>

      <div className="flex flex-col flex-grow">
        <nav className="text-base font-normal text-gray-700">
          <ul className="list-none flex flex-col gap-1 p-0 m-0">
            {menuItems.map(item => {
              const IconComponent = iconMap[item.icon];
              const hasChildren = item.children?.length > 0;
              const isSubmenuOpen = openMenus[item.path];
              return (
                <li
                  key={`${item.path}-${item.title}`}
                  className="list-none p-0 m-0"
                >
                  <div className="flex items-center">
                    <div className={hasChildren ? "flex-1 min-w-0" : "w-full"}>
                      <NavItem
                        to={item.path}
                        icon={IconComponent}
                        text={item.title}
                        collapsed={isCollapsed}
                      />
                    </div>
                    {hasChildren && !isCollapsed && (
                      <button
                        type="button"
                        onClick={e => {
                          e.preventDefault();
                          toggleSubmenu(item.path);
                        }}
                        className="absolute right-2 p-1.5 rounded-md text-white hover:bg-white/10 transition-colors shrink-0"
                        aria-label={
                          isSubmenuOpen ? "Collapse submenu" : "Expand submenu"
                        }
                        aria-expanded={isSubmenuOpen}
                      >
                        {isSubmenuOpen ? (
                          <ExpandMore className="size-4" />
                        ) : (
                          <ExpandRight className="size-4" />
                        )}
                      </button>
                    )}
                  </div>
                  {hasChildren && (
                    <div
                      className="grid transition-[grid-template-rows] duration-200 ease-out"
                      style={{
                        gridTemplateRows: isSubmenuOpen ? "1fr" : "0fr",
                      }}
                    >
                      <ul
                        className={`min-h-0 list-none p-0 m-0 flex flex-col gap-1 overflow-hidden ${isCollapsed ? "" : "pl-6"}`}
                      >
                        {item.children.map(child => {
                          const ChildIcon = iconMap[child.icon];
                          return (
                            <li
                              key={`${child.path}-${child.title}`}
                              className="list-none p-0 m-0"
                            >
                              <NavItem
                                to={child.path}
                                icon={ChildIcon}
                                text={child.title}
                                collapsed={isCollapsed}
                              />
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
      </div>

      <div
        className="mt-auto"
        onClick={() => dispatch(logout())}
        onKeyDown={e => e.key === "Enter" && dispatch(logout())}
        role="button"
        tabIndex={0}
      >
        <NavItem
          to="/"
          icon={LogoutOutlined}
          text="Logout"
          type="logout"
          collapsed={isCollapsed}
        />
      </div>
    </div>
  );
};

Sidebar.propTypes = {
  isCollapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func,
};

export default Sidebar;
